import React, { useCallback, useMemo, useState } from 'react';
import type { CoverDoc } from 'fym-shared/src/music24/coverDoc';
import type { CoverBankBackground, GetCoverEditorResponse } from '../../redux/api/fymAPI.types';
import { TypedMemo } from '../common/types';
import { useLeaveGuard } from '../smartLinks/catalog/song/page/useLeaveGuard';
import type { CoverEditorContextValue } from './CoverEditorContext';
import { CoverEditorShell } from './CoverEditorShell';
import { useCoverAutosave } from './doc/useCoverAutosave';
import { useCoverEditorDoc } from './doc/useCoverEditorDoc';
import { coverExportWatermark } from './render/exportRules';
import { EditorNotices, type FirstVersionNote } from './states/EditorNotices';
import { LeaveDialog } from './states/LeaveDialog';
import { useFirstPictureSwap } from './states/useFirstPictureSwap';
import { EditorTopBar } from './topbar/EditorTopBar';
import { UseAsCoverDialog } from './topbar/UseAsCoverDialog';
import { useDownloadCover } from './topbar/useDownloadCover';
import { useUseAsCover } from './topbar/useUseAsCover';
import { designsByAge } from './versions/designsByAge';
import { useCoverVersions } from './versions/useCoverVersions';
import { VersionsStrip } from './versions/VersionsStrip';

interface CoverEditorProps {
  accountId: number;
  catalogItemUuid: string;
  /** The open version; empty for the example artist with no saved version. */
  designUuid: string;
  initialDoc: CoverDoc;
  data: GetCoverEditorResponse;
  backgrounds: CoverBankBackground[];
  readOnly: boolean;
  /** The AI run and the note of a first open; null for a design that existed. */
  firstOpen: { runUuid: string | null; note: FirstVersionNote } | null;
  onOpenDesign: (designUuid: string) => void;
  /** Leaves the editor: back to the page that opened it. */
  onLeave: () => void;
  /** After "Use as cover": the song has the new cover. */
  onApplied: () => void;
}

/**
 * The cover editor of one open version of a song (design cover-editor b1): the shared frame
 * (`CoverEditorShell`) with the top bar of a song, the notes, the versions strip, "Use as cover"
 * and the leave dialog. It holds the document (Undo, Redo, autosave). The parent gives a new `key`
 * for another version.
 */
const CoverEditorComponent: React.FC<CoverEditorProps> = (props) => {
  const { accountId, catalogItemUuid, designUuid, initialDoc, data, backgrounds, readOnly, firstOpen } = props;
  const { onOpenDesign, onLeave, onApplied } = props;
  const editor = useCoverEditorDoc(initialDoc);
  const { state, dispatch } = editor;
  const [isUseOpen, setIsUseOpen] = useState(false);
  const canSave = !readOnly && designUuid !== '';
  const autosave = useCoverAutosave({
    accountId,
    designUuid,
    doc: state.doc,
    revision: state.revision,
    enabled: canSave,
  });
  const guard = useLeaveGuard(canSave && autosave.hasUnsaved);
  const [isLeaving, setIsLeaving] = useState(false);
  const first = useFirstPictureSwap({
    runUuid: firstOpen?.runUuid ?? null,
    initialNote: firstOpen?.note ?? null,
    pictures: data.pictures,
    revision: state.revision,
    dispatch,
  });
  const watermark = coverExportWatermark({ apiWatermark: data.watermark, plan: data.plan });
  const names = { artist: data.song.artistName, title: data.song.title };
  const download = useDownloadCover({ doc: state.doc, watermark, ...names });
  const applyCover = useUseAsCover({
    accountId,
    catalogItemUuid,
    designUuid,
    doc: state.doc,
    watermark,
    ...names,
    flush: autosave.flush,
    onDone: onApplied,
  });
  const versions = useCoverVersions({
    accountId,
    catalogItemUuid,
    doc: state.doc,
    flush: autosave.flush,
    onOpenDesign,
  });

  const startUse = useCallback(() => {
    applyCover.clearError();
    // A song with no cover has nothing to replace: no question.
    if (data.song.artworkUrl === null) {
      applyCover.apply().catch(console.error);
    }
    setIsUseOpen(true);
  }, [applyCover, data.song.artworkUrl]);
  const saveAndLeave = useCallback(() => {
    setIsLeaving(true);
    autosave
      .flush()
      .then(guard.proceed)
      .catch(console.error)
      .finally(() => setIsLeaving(false));
  }, [autosave, guard.proceed]);

  const version = useMemo(
    () => designsByAge(data.designs).find(({ design }) => design.uuid === designUuid)?.number ?? 1,
    [data.designs, designUuid]
  );
  const base = useMemo<Omit<CoverEditorContextValue, 'tool' | 'setTool'>>(
    () => ({
      mode: 'song',
      accountId,
      catalogItemUuid,
      designUuid,
      data,
      backgrounds,
      state,
      dispatch,
      canUndo: editor.canUndo,
      canRedo: editor.canRedo,
      readOnly,
    }),
    [
      accountId,
      backgrounds,
      catalogItemUuid,
      data,
      designUuid,
      dispatch,
      editor.canRedo,
      editor.canUndo,
      readOnly,
      state,
    ]
  );
  const renderTopBar = useCallback(
    (zoom: number, onZoom: (zoom: number) => void) => (
      <EditorTopBar
        saveState={autosave.saveState}
        version={version}
        zoom={zoom}
        onZoom={onZoom}
        onBack={onLeave}
        onDownload={download.download}
        isDownloading={download.isBusy}
        onUseAsCover={startUse}
        isApplying={applyCover.isBusy}
      />
    ),
    [applyCover.isBusy, autosave.saveState, download.download, download.isBusy, onLeave, startUse, version]
  );

  return (
    <CoverEditorShell
      base={base}
      renderTopBar={renderTopBar}
      notices={<EditorNotices firstVersion={first.note} onDismissFirst={first.dismiss} />}
      bottom={
        <VersionsStrip
          onOpen={onOpenDesign}
          onNew={versions.create}
          onDelete={versions.remove}
          isBusy={versions.isBusy}
        />
      }
      hasWatermark={watermark}
    >
      <UseAsCoverDialog
        isOpen={isUseOpen && (data.song.artworkUrl !== null || applyCover.error !== null)}
        accountId={accountId}
        catalogItemUuid={catalogItemUuid}
        doc={state.doc}
        currentCoverUrl={data.song.artworkUrl}
        songTitle={data.song.title}
        watermark={watermark}
        isBusy={applyCover.isBusy}
        error={applyCover.error}
        onConfirm={() => {
          applyCover.apply().catch(console.error);
        }}
        onCancel={() => setIsUseOpen(false)}
      />
      <LeaveDialog
        isOpen={guard.isBlocked}
        isBusy={isLeaving}
        isSaveFailed={autosave.saveState === 'failed' || autosave.saveState === 'invalid'}
        onSaveAndLeave={saveAndLeave}
        onStay={guard.cancel}
      />
    </CoverEditorShell>
  );
};

export const CoverEditor = TypedMemo(CoverEditorComponent);
